import React from "react";
import { Box, Card, Divider, IconButton, Stack, Typography, useMediaQuery, useTheme } from "@mui/material";
import { formatReadableDateWithTime, toCurrencyFormat } from "utils/formatter";
import { TravelOffer } from "types/TravelOffer";
import PersonIcon from "@mui/icons-material/Person";
import PlaceIcon from "@mui/icons-material/Place";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import { GpsFixed, AccessTime, Route, People, Visibility } from "@mui/icons-material";
import { Column } from "components/common/Layout/Column";
import { Row } from "components/common/Layout/Row";
import dayjs from "dayjs";

type TravelCardProps = {
  travelOffer: TravelOffer;
  onClick?: (t: TravelOffer) => void;
  isSelected?: boolean;
  onEyeClick: (t: TravelOffer) => void;
};

export const TravelCard: React.FC<TravelCardProps> = ({ travelOffer, onClick, isSelected, onEyeClick }) => {
  const { palette, breakpoints } = useTheme();
  const isXs = useMediaQuery(breakpoints.down("sm"));

  return (
    <Card
      sx={{
        margin: 1,
        padding: 1,
        display: "flex",
        flex: 1,
        position: "relative",
        minWidth: isXs ? "100%" : 280,
        cursor: "pointer",
        backgroundColor: isSelected ? palette.primary.light : "white",
      }}
      elevation={4}
    >
      <Box
        sx={{
          display: "flex",
          flex: 1,
          flexDirection: "column",
          ml: 2,
          mr: 2,
          mt: 2,
        }}
        onClick={() => onClick && onClick(travelOffer)}
      >
        <Typography textAlign="left" variant="subtitle2" color="primary">
          {formatReadableDateWithTime(travelOffer.createdAt)}
        </Typography>

        <Row>
          <Column gap={1} flex={1}>
            <Stack direction="row" spacing={1} alignItems="center">
              <PersonIcon fontSize="small" />
              <Typography sx={{ fontSize: 14 }}>{travelOffer.user.name}</Typography>
            </Stack>

            <Row>
              <Row gap={0.5}>
                <People fontSize="small" />
                <Typography sx={{ fontSize: 14 }}>{travelOffer.passengers}</Typography>
              </Row>
              <Row alignItems="center" gap={2}>
                <img
                  src={travelOffer.vehicleType.icon}
                  alt="car"
                  width={32}
                  height={32}
                  style={{
                    objectFit: "contain",
                  }}
                />
                <Typography variant="h6">{travelOffer.vehicleType.type}</Typography>
              </Row>
            </Row>
            <Stack direction="row" spacing={1} alignItems="center">
              <GpsFixed fontSize="small" />
              <Typography sx={{ fontSize: 14 }}>{travelOffer.originAddress}</Typography>
            </Stack>
            <Stack direction="row" spacing={1} alignItems="center">
              <PlaceIcon fontSize="small" />
              <Typography sx={{ fontSize: 14 }}>{travelOffer.destinationAddress}</Typography>
            </Stack>
          </Column>
          <Column justifyContent="center" alignItems="center">
            <IconButton
              onClick={(e) => {
                e.stopPropagation();
                onEyeClick(travelOffer);
              }}
            >
              <Visibility fontSize="large" />
            </IconButton>
          </Column>
        </Row>
        <Divider sx={{ m: 1 }} />
        <Row>
          <Row gap={0.5}>
            <AttachMoneyIcon fontSize="small" />
            <Typography sx={{ fontSize: 14 }}>{toCurrencyFormat(travelOffer.price)}</Typography>
          </Row>
          <Row gap={0.5}>
            <AccessTime fontSize="small" />
            <Typography sx={{ fontSize: 14 }}>{dayjs.duration(travelOffer.time, "seconds").humanize()}</Typography>
          </Row>
          <Row gap={0.5}>
            <Route fontSize="small" />
            <Typography sx={{ fontSize: 14 }}>{(travelOffer.distance / 1000).toFixed(2)} km</Typography>
          </Row>
        </Row>
        <Column></Column>
      </Box>
      <Box
        sx={{
          position: "absolute",
          right: 0,
          top: 0,
          backgroundColor: palette.primary.main,
          paddingTop: 1,
          paddingBottom: 1,
          paddingLeft: 2,
          paddingRight: 2,
          borderRadius: "0px 0px 0px 10px",
        }}
      >
        <Typography color="white" fontWeight={600}>
          {travelOffer.id}
        </Typography>
      </Box>
    </Card>
  );
};
